import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { extractScheduleFromImage, type ExtractedRow } from "@/lib/schedule.functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Camera, Loader2, Plus, Trash2, Save, Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { formatDate } from "@/lib/date-format";

type Row = ExtractedRow & { id?: string };

export function ClassScheduleSection() {
  return <ClassScheduleInner />;
}


function ClassScheduleInner() {
  const { t } = useTranslation();
  const dayLabels = t("library.schedule.dayLabels", { returnObjects: true }) as string[];
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const extract = useServerFn(extractScheduleFromImage);
  const [draft, setDraft] = useState<Row[] | null>(null);

  const { data: rows = [] } = useQuery({
    queryKey: ["class_schedules"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return [];
      const { data, error } = await supabase
        .from("class_schedules")
        .select("id,day_of_week,period,start_time,end_time,subject,room")
        .order("day_of_week")
        .order("period");
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const grouped = useMemo(() => {
    const m = new Map<number, Row[]>();
    for (const r of rows) {
      const arr = m.get(r.day_of_week) ?? [];
      arr.push(r);
      m.set(r.day_of_week, arr);
    }
    return m;
  }, [rows]);

  const extractMutation = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > 8 * 1024 * 1024) throw new Error(t("library.schedule.photoTooLarge"));
      const buf = await file.arrayBuffer();
      let bin = "";
      const bytes = new Uint8Array(buf);
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
      const b64 = btoa(bin);
      return extract({ data: { imageBase64: b64, mediaType: file.type || "image/jpeg" } });
    },
    onSuccess: (r) => {
      setDraft(r.rows);
      toast.success(t("library.schedule.extractedSuccess", { count: r.rows.length }));
    },
    onError: (e: Error) => toast.error(e.message || t("library.schedule.extractError")),
  });

  const saveMutation = useMutation({
    mutationFn: async (toSave: Row[]) => {
      const { data: u } = await supabase.auth.getUser();
      const uid = u.user?.id;
      if (!uid) throw new Error(t("library.schedule.notAuthenticated"));
      // 1) Validate + normalize BEFORE touching the DB, so we never wipe on invalid input.
      const timeRe = /^([01]?\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;
      const norm = (t_str: string) => {
        const m = t_str.trim().match(/^(\d{1,2}):(\d{2})/);
        if (!m) return null;
        const hh = String(Math.min(23, parseInt(m[1], 10))).padStart(2, "0");
        return `${hh}:${m[2]}:00`;
      };
      const seen = new Set<string>();
      const cleaned: {
        user_id: string; day_of_week: number; period: number;
        start_time: string; end_time: string; subject: string; room: string | null;
      }[] = [];
      for (const r of toSave) {
        const subj = (r.subject ?? "").trim();
        if (!subj) continue; // skip empty rows silently
        if (!timeRe.test(r.start_time) || !timeRe.test(r.end_time)) {
          throw new Error(t("library.schedule.invalidTime", { subject: subj }));
        }
        const day = Number(r.day_of_week);
        const period = Number(r.period);
        if (!Number.isFinite(day) || day < 1 || day > 7) throw new Error(t("library.schedule.invalidDay", { subject: subj }));
        if (!Number.isFinite(period) || period < 1) throw new Error(t("library.schedule.invalidPeriod", { subject: subj }));
        const key = `${day}-${period}`;
        if (seen.has(key)) throw new Error(t("library.schedule.duplicatePeriod", { day: dayLabels[day - 1], period }));
        seen.add(key);
        cleaned.push({
          user_id: uid,
          day_of_week: day,
          period,
          start_time: norm(r.start_time)!,
          end_time: norm(r.end_time)!,
          subject: subj,
          room: (r.room ?? "").trim() || null,
        });
      }
      // 2) Upsert on the unique (user_id, day_of_week, period) so nothing is lost mid-flight.
      if (cleaned.length) {
        const { error } = await supabase
          .from("class_schedules")
          .upsert(cleaned, { onConflict: "user_id,day_of_week,period" });
        if (error) throw new Error(error.message);
      }
      // 3) Delete only rows that are no longer present in the draft.
      const { data: existing, error: exErr } = await supabase
        .from("class_schedules")
        .select("id,day_of_week,period")
        .eq("user_id", uid);
      if (exErr) throw new Error(exErr.message);
      const keepKeys = new Set(cleaned.map((r) => `${r.day_of_week}-${r.period}`));
      const toDelete = (existing ?? [])
        .filter((r) => !keepKeys.has(`${r.day_of_week}-${r.period}`))
        .map((r) => r.id);
      if (toDelete.length) {
        const { error: delErr } = await supabase
          .from("class_schedules")
          .delete()
          .in("id", toDelete);
        if (delErr) throw new Error(delErr.message);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["class_schedules"] });
      setDraft(null);
      toast.success(t("library.schedule.saved"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) extractMutation.mutate(f);
    e.target.value = "";
  }

  function startEdit() {
    setDraft(rows.map((r) => ({ ...r })));
  }
  function addRow() {
    setDraft((d) => [...(d ?? []), {
      day_of_week: 1, period: (d?.length ?? 0) + 1,
      start_time: "08:00", end_time: "08:55", subject: "", room: "",
    }]);
  }
  function updateDraft(i: number, patch: Partial<Row>) {
    setDraft((d) => d?.map((r, idx) => idx === i ? { ...r, ...patch } : r) ?? null);
  }
  function removeDraft(i: number) {
    setDraft((d) => d?.filter((_, idx) => idx !== i) ?? null);
  }

  return (
    <Card className="card-soft border-0 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold">{t("library.schedule.title")}</p>
          </div>
          <p className="text-xs text-muted-foreground">{t("library.schedule.subtitle")}</p>
        </div>
        <div className="flex gap-1.5">
          {draft && (
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)} className="rounded-xl">{t("library.schedule.cancel")}</Button>
          )}
          {draft ? (
            <Button size="sm" onClick={() => saveMutation.mutate(draft)} disabled={saveMutation.isPending} className="rounded-xl">
              {saveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            </Button>
          ) : rows.length > 0 ? (
            <Button size="sm" variant="secondary" onClick={startEdit} className="rounded-xl">{t("library.schedule.edit")}</Button>
          ) : null}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onPick} />
          <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()} disabled={extractMutation.isPending} className="rounded-xl">
            {extractMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
            <span className="ml-1">{rows.length ? t("library.schedule.newPhoto") : t("library.schedule.takePhoto")}</span>
          </Button>
        </div>
      </div>

      {draft ? (
        <div className="space-y-1.5">
          {draft.map((r, i) => (
            <div key={i} className="grid grid-cols-2 items-center gap-1.5 rounded-lg bg-muted/40 p-1.5 sm:grid-cols-[auto_auto_minmax(0,1fr)_auto_auto]">
              <select value={r.day_of_week} onChange={(e) => updateDraft(i, { day_of_week: Number(e.target.value) })} aria-label={t("library.schedule.day", { defaultValue: "Giorno" })} className="min-w-0 rounded-md border bg-background px-1 py-1 text-xs">
                {dayLabels.map((d, k) => <option key={k} value={k + 1}>{d}</option>)}
              </select>
              <Input value={r.start_time} onChange={(e) => updateDraft(i, { start_time: e.target.value })} aria-label={t("library.schedule.timePlaceholder")} className="h-8 w-full min-w-0 text-xs sm:w-20" placeholder={t("library.schedule.timePlaceholder")} />
              <Input value={r.subject} onChange={(e) => updateDraft(i, { subject: e.target.value })} aria-label={t("library.schedule.subjectPlaceholder")} className="h-8 min-w-0 text-xs" placeholder={t("library.schedule.subjectPlaceholder")} />
              <Input value={r.room ?? ""} onChange={(e) => updateDraft(i, { room: e.target.value })} aria-label={t("library.schedule.roomPlaceholder")} className="h-8 w-full min-w-0 text-xs sm:w-16" placeholder={t("library.schedule.roomPlaceholder")} />
              <Button size="icon" variant="ghost" onClick={() => removeDraft(i)} aria-label={t("library.schedule.removePeriod", { defaultValue: "Rimuovi ora" })} className="h-9 w-9 justify-self-end"><Trash2 className="h-3.5 w-3.5" aria-hidden /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addRow} className="w-full rounded-xl"><Plus className="mr-1 h-3.5 w-3.5" aria-hidden /> {t("library.schedule.addPeriod")}</Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("library.schedule.noScheduleYet")}</p>
      ) : (
        <div className="grid grid-cols-2 gap-1 text-xs sm:grid-cols-3 lg:grid-cols-6">
          {dayLabels.slice(0, 6).map((d, k) => {
            const day = k + 1;
            const list = grouped.get(day) ?? [];
            return (
              <div key={day} className="min-h-16 min-w-0 rounded-lg bg-muted/30 p-1.5">
                <p className="mb-1 break-words text-center text-xs font-semibold text-muted-foreground">{d}</p>
                {list.map((r) => (
                  <div key={r.id} className="mb-1 min-w-0 rounded bg-background px-1 py-0.5">
                    <p className="text-xs tabular-nums text-muted-foreground">{r.start_time.slice(0, 5)}</p>
                    <p className="break-words text-xs font-medium">{r.subject}</p>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

    </Card>
  );
}

/* -------------------- Homework -------------------- */

type Hw = { id: string; due_date: string; subject: string | null; title: string; done: boolean };

export function HomeworkSection({ selected }: { selected: Date }) {
  const { t } = useTranslation();
  const loading = false;
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");

  const dueStr = toLocalDate(selected);

  const { data: items = [] } = useQuery({
    queryKey: ["homework", dueStr],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return [];
      const { data, error } = await supabase
        .from("homework")
        .select("id,due_date,subject,title,done")
        .eq("due_date", dueStr)
        .order("done").order("created_at");
      if (error) throw error;
      return (data ?? []) as Hw[];
    },
  });

  const add = useMutation({
    mutationFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      const uid = u.user?.id;
      if (!uid) throw new Error(t("library.schedule.notAuthenticated"));
      const { error } = await supabase.from("homework").insert({
        user_id: uid, due_date: dueStr, subject: subject.trim() || null, title: title.trim(),
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { setTitle(""); setSubject(""); qc.invalidateQueries({ queryKey: ["homework"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      const { error } = await supabase.from("homework").update({ done }).eq("id", id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["homework"] }),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("homework").delete().eq("id", id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["homework"] }),
  });

  if (loading) return null;

  return (
    <Card className="card-soft border-0 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">{t("library.schedule.homeworkFor", { date: formatDate(selected, { weekday: "long", day: "numeric", month: "short" }) })}</p>
          <p className="text-xs text-muted-foreground">
            {t("library.schedule.reminderInApp")}
          </p>
        </div>
      </div>
      {items.length > 0 && (
        <ul className="space-y-1">
          {items.map((h) => (
            <li key={h.id} className="flex min-h-11 flex-wrap items-center gap-2 rounded-lg bg-muted/40 p-2 text-sm">
              <input type="checkbox" checked={h.done} onChange={(e) => toggle.mutate({ id: h.id, done: e.target.checked })} aria-label={`${t("library.schedule.markDone", { defaultValue: "Segna come fatto" })}: ${h.title}`} className="h-4 w-4 shrink-0" />
              <span className={`min-w-[8rem] flex-1 break-words ${h.done ? "text-muted-foreground line-through" : ""}`}>
                {h.subject ? <span className="mr-1 font-medium">{h.subject}:</span> : null}
                {h.title}
              </span>
              <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" aria-label={`${t("library.schedule.deleteTask", { defaultValue: "Elimina" })}: ${h.title}`} onClick={() => del.mutate(h.id)}><Trash2 className="h-3 w-3" aria-hidden /></Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-1.5">
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} aria-label={t("library.schedule.subjectShort")} placeholder={t("library.schedule.subjectShort")} className="h-9 w-24 min-w-0 text-xs" />
        <Input value={title} onChange={(e) => setTitle(e.target.value)} aria-label={t("library.schedule.taskPlaceholder")} placeholder={t("library.schedule.taskPlaceholder")} className="h-9 min-w-[8rem] flex-1 text-xs"
          onKeyDown={(e) => { if (e.key === "Enter" && title.trim()) add.mutate(); }} />
        <Button size="sm" onClick={() => add.mutate()} disabled={!title.trim() || add.isPending} aria-label={t("library.schedule.addPeriod")} className="h-9 shrink-0 rounded-xl">
          <Plus className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>

    </Card>
  );
}

function toLocalDate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/* -------------------- Tomorrow homework toast (in-app reminder) -------------------- */

export function useTomorrowHomeworkReminder() {
  const { t } = useTranslation();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user || cancelled) return;
      const t_date = new Date();
      t_date.setDate(t_date.getDate() + 1);
      const dueStr = toLocalDate(t_date);
      const key = `hw-reminder:${u.user.id}:${dueStr}`;
      if (localStorage.getItem(key)) return;
      const { data } = await supabase
        .from("homework")
        .select("id,title,subject")
        .eq("due_date", dueStr)
        .eq("done", false);
      if (cancelled) return;
      const list = (data ?? []) as { title: string; subject: string | null }[];
      if (list.length === 0) return;
      localStorage.setItem(key, "1");
      const preview = list.slice(0, 3).map((h) => h.subject ? `${h.subject}: ${h.title}` : h.title).join(" • ");
      toast(t("library.schedule.reminderToastTitle", { count: list.length }), {
        description: preview + (list.length > 3 ? t("library.schedule.reminderToastMore", { count: list.length - 3 }) : ""),
        duration: 8000,
      });
    })();
    return () => { cancelled = true; };
  }, [t]);
}
